
// Enhanced device capability detection
export interface DeviceCapabilities {
  deviceMemoryGB: number;
  cpuCores: number;
  isMobile: boolean;
  browserName: string;
  hasLimitedMemory: boolean;
  availableMemoryMB: number;
  storageQuotaMB: number;
  supportsCompressionAPI: boolean;
  supportsBlobManager: boolean;
}

// Advanced detection of device capabilities
export async function detectDeviceCapabilities(): Promise<DeviceCapabilities> {
  const capabilities: DeviceCapabilities = {
    deviceMemoryGB: 4, // Default assumption
    cpuCores: 4,       // Default assumption
    isMobile: false,
    browserName: 'unknown',
    hasLimitedMemory: false,
    availableMemoryMB: 1024, // Default assumption
    storageQuotaMB: 1024,    // Default assumption
    supportsCompressionAPI: false,
    supportsBlobManager: false
  };
  
  try {
    // Check for device memory API
    if ('navigator' in globalThis && 'deviceMemory' in navigator) {
      // @ts-ignore - deviceMemory is not in all TS definitions
      capabilities.deviceMemoryGB = (navigator as any).deviceMemory || 4;
    }
    
    // Check for hardware concurrency (CPU cores)
    if ('navigator' in globalThis && 'hardwareConcurrency' in navigator) {
      capabilities.cpuCores = navigator.hardwareConcurrency;
    }
    
    // Check if it's a mobile device
    if ('navigator' in globalThis && 'userAgent' in navigator) {
      const userAgent = navigator.userAgent.toLowerCase();
      capabilities.isMobile = /android|webos|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent);
      
      // Detect browser
      if (userAgent.indexOf('chrome') > -1) capabilities.browserName = 'chrome';
      else if (userAgent.indexOf('safari') > -1) capabilities.browserName = 'safari';
      else if (userAgent.indexOf('firefox') > -1) capabilities.browserName = 'firefox';
      else if (userAgent.indexOf('edge') > -1) capabilities.browserName = 'edge';
    }
    
    // Check available memory
    if ('performance' in globalThis && 'memory' in performance) {
      // @ts-ignore - memory is not in all TS definitions
      const memory = (performance as any).memory;
      if (memory) {
        const totalHeapSize = memory.jsHeapSizeLimit;
        const usedHeapSize = memory.usedJSHeapSize;
        const availableHeap = totalHeapSize - usedHeapSize;
        capabilities.availableMemoryMB = Math.floor(availableHeap / (1024 * 1024));
      }
    }
    
    // Check storage quota
    if ('navigator' in globalThis && 'storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        if (estimate.quota) {
          capabilities.storageQuotaMB = Math.floor(estimate.quota / (1024 * 1024));
        }
      } catch (e) {
        console.warn('Error estimating storage quota:', e);
      }
    }
    
    // Check for Compression API support
    capabilities.supportsCompressionAPI = 'CompressionStream' in globalThis;
    
    // Check for experimental BlobManager API (not widely supported yet)
    // @ts-ignore - BlobManager is experimental
    capabilities.supportsBlobManager = 'BlobManager' in globalThis;
    
    // Determine if this is a limited memory device
    capabilities.hasLimitedMemory = capabilities.deviceMemoryGB < 4 || 
                                   capabilities.availableMemoryMB < 200 ||
                                   capabilities.isMobile;
    
    return capabilities;
  } catch (error) {
    console.error('Error detecting device capabilities:', error);
    return capabilities; // Return default capabilities
  }
}
